/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useEffect, useState } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useRouteType, type RouteType } from '@/analytics/route-analytics';
import { cn } from '@/lib/utils';

/**
 * Lazy-load Zendesk chat on click (W-23789028, homepage TBT).
 *
 * Zendesk is the single biggest homepage main-thread offender (~481 ms CPU) and is loaded on every
 * page by the GTM container (Custom HTML tags firing on *All Pages*). This facade removes that cost
 * from page load: the storefront renders a cheap, pixel-matched launcher button and only loads the
 * real widget when the shopper actually clicks it.
 *
 * Flow:
 *  1. This facade renders site-wide (server-rendered, present at first paint), gated on the same
 *     GTM config (`engagement.gtm.containerId` + `allowedHosts`) the GtmBootstrap uses.
 *  2. On click it pushes `{ event: 'zendesk_open' }` to `window.dataLayer` — the single string of
 *     contract with GTM. In the container, the Zendesk tag's trigger is changed from *All Pages* to
 *     a Custom Event trigger matching this event (the entire GTM-side change).
 *  3. GTM fires the Zendesk tag → the snippet loads → `window.zE` becomes available. The facade
 *     opens the real widget (`zE('messenger','open')`) and dismounts itself once the real launcher
 *     is up (`messenger:on 'open'`, with a MutationObserver + grace-timer as fallbacks).
 *
 * Deploy-choreography safety (self-suppression): shortly after mount the facade watches for Zendesk
 * loading *without* a click. Before the GTM trigger flip, Zendesk still auto-loads on page view, so
 * the facade removes itself and defers to the real launcher — no double launcher, no regression. The
 * storefront can therefore ship first; flipping the GTM trigger later activates the TBT win.
 *
 * Design spec: docs/superpowers/specs/2026-08-31-lazy-load-zendesk-design.md
 */

/** The dataLayer event the storefront pushes on click; GTM's Zendesk tag triggers on this. */
export const ZENDESK_OPEN_EVENT = 'zendesk_open';

// Timings (ms).
const POLL_INTERVAL_MS = 100; // how often we re-check for window.zE after a click
const LOADING_TIMEOUT_MS = 10_000; // if zE never appears (GTM blocked/consent denied), revert to idle
const HANDOFF_GRACE_MS = 4_000; // after zE is ready, dismiss even if no open/launcher signal fires
const SELF_SUPPRESS_POLL_MS = 250; // self-suppression poll cadence
const SELF_SUPPRESS_WINDOW_MS = 12_000; // how long to watch for an independent (pre-flip) Zendesk load

// Visual fidelity — matches the live Zendesk launcher on dev.baggallini.com. These are an
// INTENTIONAL exception to the design-token rule: the facade must be indistinguishable from a
// third-party widget, not themed to the storefront. All values sampled from the live widget's
// launcher iframe (64px circle, cream fill, dark-brown glyph, teal-tinted shadow). See the spec's
// "visual fidelity" acceptance.
const LAUNCHER_BG = '#f0e5db'; // brand-themed cream fill (rgb(240,229,219))
const LAUNCHER_ICON = '#423224'; // dark-brown chat glyph (rgb(66,50,36))
const LAUNCHER_SHADOW = 'rgba(23, 73, 77, 0.15) 0px 20px 30px 0px';

const PROACTIVE_MESSAGE = 'Hi. Need any assistance?';
const PROACTIVE_MESSAGE_EXCLUDED_ROUTE_TYPES: readonly RouteType[] = ['home'];

// Matches Zendesk's white proactive-message surfaces. The vendor-specific radius and shadow do
// not have equivalent project tokens, so they remain localized Tailwind arbitrary utilities.
const PROACTIVE_MESSAGE_SURFACE_CLASSES =
    'rounded-[1.25rem] border-0 bg-white text-black shadow-[0_20px_30px_0_rgba(23,73,77,0.15)]';

type Status = 'idle' | 'loading' | 'dismissed';

/** True when the Zendesk messaging API is available on the page. */
function isZendeskLoaded(): boolean {
    return typeof window !== 'undefined' && typeof window.zE === 'function';
}

/**
 * Gate wrapper: renders nothing unless GTM (and therefore Zendesk) is configured for this
 * deployment. `containerId` is read identically on the server and client, so this decision is
 * hydration-stable. Host-level (`allowedHosts`) and self-suppression are handled after mount by the
 * inner component to avoid a hydration mismatch.
 */
export function ZendeskLauncherFacade(): React.ReactElement | null {
    const config = useConfig();
    const gtm = config?.engagement?.gtm;
    if (!gtm?.containerId?.trim()) return null;
    return <ZendeskLauncherFacadeInner allowedHosts={gtm.allowedHosts ?? []} />;
}

function ZendeskLauncherFacadeInner({ allowedHosts }: { allowedHosts: string[] }): React.ReactElement | null {
    const [status, setStatus] = useState<Status>('idle');
    const [promptDismissed, setPromptDismissed] = useState(false);
    const routeType = useRouteType();

    // Host gate (post-mount so SSR/client initial renders match). On hosts GTM is not allowed to run
    // (local dev, MRT preview), Zendesk never loads, so a facade here would be dead — remove it.
    useEffect(() => {
        if (allowedHosts.length === 0 || typeof window === 'undefined') return;
        if (!allowedHosts.includes(window.location.host)) setStatus('dismissed');
    }, [allowedHosts]);

    // Self-suppression: if Zendesk loads on its own (i.e. the GTM trigger is still *All Pages*),
    // defer to the real launcher instead of showing our facade beside it.
    useEffect(() => {
        if (status !== 'idle' || typeof window === 'undefined') return;
        if (isZendeskLoaded()) {
            setStatus('dismissed');
            return;
        }
        const pollId = setInterval(() => {
            if (isZendeskLoaded()) setStatus('dismissed');
        }, SELF_SUPPRESS_POLL_MS);
        const stopId = setTimeout(() => clearInterval(pollId), SELF_SUPPRESS_WINDOW_MS);
        return () => {
            clearInterval(pollId);
            clearTimeout(stopId);
        };
    }, [status]);

    // Handoff: after a click, wait for Zendesk, open it, then dismount the facade.
    useEffect(() => {
        if (status !== 'loading' || typeof window === 'undefined') return;
        let settled = false;
        let pollId: ReturnType<typeof setInterval> | undefined;
        let graceId: ReturnType<typeof setTimeout> | undefined;
        let observer: MutationObserver | undefined;

        const dismiss = () => {
            if (settled) return;
            settled = true;
            setStatus('dismissed');
        };

        const openWhenReady = (): boolean => {
            const zE = window.zE;
            if (typeof zE !== 'function') return false;
            // Subscribe before opening so the open we trigger is caught as the dismiss signal.
            try {
                zE('messenger:on', 'open', dismiss);
            } catch {
                /* zE API shape differs — fall back to the observer/grace timer below */
            }
            try {
                zE('messenger', 'open');
            } catch {
                /* queued command rejected — observer/grace timer still dismiss the facade */
            }
            // Fallback 1: dismiss as soon as the real launcher iframe mounts.
            observer = new MutationObserver(() => {
                if (document.querySelector('iframe#launcher')) dismiss();
            });
            observer.observe(document.body, { childList: true, subtree: true });
            // Fallback 2: Zendesk is loaded — never leave a double launcher, so dismiss regardless.
            graceId = setTimeout(dismiss, HANDOFF_GRACE_MS);
            return true;
        };

        if (!openWhenReady()) {
            pollId = setInterval(() => {
                if (openWhenReady() && pollId) clearInterval(pollId);
            }, POLL_INTERVAL_MS);
        }

        // Zendesk never became available (GTM blocked / consent denied) — revert so the user can retry.
        const timeoutId = setTimeout(() => {
            if (settled) return;
            if (pollId) clearInterval(pollId);
            setStatus('idle');
        }, LOADING_TIMEOUT_MS);

        return () => {
            if (pollId) clearInterval(pollId);
            if (graceId) clearTimeout(graceId);
            clearTimeout(timeoutId);
            observer?.disconnect();
        };
    }, [status]);

    if (status === 'dismissed') return null;

    const isLoading = status === 'loading';
    const showProactiveMessage =
        status === 'idle' && !promptDismissed && !PROACTIVE_MESSAGE_EXCLUDED_ROUTE_TYPES.includes(routeType);

    const handleClick = () => {
        if (status !== 'idle') return; // guard re-entrancy (double-click / mid-load)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: ZENDESK_OPEN_EVENT });
        setStatus('loading');
    };

    return (
        <div className="fixed bottom-4 right-4 z-[999999] flex flex-col items-end gap-2">
            {showProactiveMessage && (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        aria-label="Close message from company"
                        onClick={() => setPromptDismissed(true)}
                        className={cn(
                            PROACTIVE_MESSAGE_SURFACE_CLASSES,
                            'flex shrink-0 cursor-pointer items-center justify-center p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                        )}>
                        <svg
                            aria-hidden="true"
                            className="size-4"
                            xmlns="http://www.w3.org/2000/svg"
                            focusable="false"
                            viewBox="0 0 16 16">
                            <path fill="none" stroke="currentColor" strokeLinecap="round" d="M3 13L13 3m0 10L3 3" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        aria-label={PROACTIVE_MESSAGE}
                        onClick={handleClick}
                        className={cn(
                            PROACTIVE_MESSAGE_SURFACE_CLASSES,
                            'shrink-0 cursor-pointer whitespace-nowrap px-3 py-3 font-[system-ui] text-sm leading-5 font-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                        )}>
                        {PROACTIVE_MESSAGE}
                    </button>
                </div>
            )}
            <button
                type="button"
                // Matches the real widget's launcher role (it labels its iframe "Open messaging window").
                aria-label="Open chat"
                aria-busy={isLoading}
                onClick={handleClick}
                className="flex size-16 items-center justify-center rounded-full transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                style={{ backgroundColor: LAUNCHER_BG, boxShadow: LAUNCHER_SHADOW, color: LAUNCHER_ICON }}>
                {isLoading ? (
                    <span
                        aria-hidden="true"
                        className="h-6 w-6 animate-spin rounded-full border-2"
                        style={{ borderColor: 'rgba(66, 50, 36, 0.25)', borderTopColor: LAUNCHER_ICON }}
                    />
                ) : (
                    <svg
                        aria-hidden="true"
                        width="36"
                        height="36"
                        viewBox="0 0 24 24"
                        fill={LAUNCHER_ICON}
                        xmlns="http://www.w3.org/2000/svg">
                        {/* Rounded speech bubble with a tail — the exact Zendesk messaging launcher glyph
                            (path + mirror transform) sampled from the live widget on dev.baggallini.com. */}
                        <path
                            d="M10,18 L6,22 L6,18 L10,18 Z M17,6 C19.7614237,6 22,8.23857625 22,11 C22,13.7614237 19.7614237,16 17,16 L17,16 L7,16 C4.23857625,16 2,13.7614237 2,11 C2,8.23857625 4.23857625,6 7,6 L7,6 Z"
                            transform="translate(12, 14) scale(-1, 1) translate(-12, -14)"
                        />
                    </svg>
                )}
            </button>
        </div>
    );
}

// window.dataLayer is declared in ga4-datalayer.ts; declare only the Zendesk messaging API here.
declare global {
    interface Window {
        /** Zendesk Web Widget (messaging) command queue, defined by the Zendesk snippet once loaded. */
        zE?: (...args: unknown[]) => void;
    }
}
